/**
 * Temp Mail API Service
 * CLI-side client that calls the jai1 server API
 */
import type { Jai1Config } from '../types/framework.types.js';

export interface TempMailAccount {
    address: string;
    created_at: string;
}

export interface TempMailMessage {
    id: string;
    from: { name: string; address: string };
    to: { name: string; address: string }[];
    subject: string;
    intro: string;
    seen: boolean;
    hasAttachments: boolean;
    createdAt: string;
}

export interface TempMailMessageDetail extends TempMailMessage {
    text: string;
    html: string[];
    cc: string[];
}

export class TempMailApiService {
    private readonly baseUrl: string;
    private readonly accessKey: string;

    constructor(config: Jai1Config) {
        this.baseUrl = config.apiUrl.replace(/\/$/, '');
        this.accessKey = config.accessKey;
    }

    private async request<T>(
        path: string,
        options: RequestInit = {},
    ): Promise<{ success: boolean; data?: T; error?: string }> {
        const url = `${this.baseUrl}/api/temp-mail${path}`;
        const res = await fetch(url, {
            ...options,
            headers: {
                'Content-Type': 'application/json',
                'JAI1-Access-Key': this.accessKey,
                ...options.headers,
            },
        });

        const body = await res.json() as { success: boolean; data?: T; error?: string };
        return body;
    }

    async createAccount(name?: string): Promise<{ success: boolean; data?: TempMailAccount; error?: string }> {
        return this.request<TempMailAccount>('/accounts', {
            method: 'POST',
            body: JSON.stringify({ name }),
        });
    }

    async listAccounts(): Promise<{ success: boolean; data?: TempMailAccount[]; error?: string }> {
        return this.request<TempMailAccount[]>('/accounts');
    }

    async getRandomAccount(): Promise<{ success: boolean; data?: TempMailAccount; error?: string }> {
        return this.request<TempMailAccount>('/accounts/random');
    }

    async getInbox(
        address: string,
        limit = 1,
    ): Promise<{ success: boolean; data?: TempMailMessage[]; error?: string }> {
        const enc = encodeURIComponent(address);
        return this.request<TempMailMessage[]>(`/accounts/${enc}/inbox?limit=${limit}`);
    }

    async getMessage(
        address: string,
        messageId: string,
    ): Promise<{ success: boolean; data?: TempMailMessageDetail; error?: string }> {
        const enc = encodeURIComponent(address);
        return this.request<TempMailMessageDetail>(`/accounts/${enc}/messages/${messageId}`);
    }

    async purgeMessages(
        address: string,
    ): Promise<{ success: boolean; data?: { deleted: number }; error?: string }> {
        const enc = encodeURIComponent(address);
        return this.request<{ deleted: number }>(`/accounts/${enc}/messages`, {
            method: 'DELETE',
        });
    }

    async deleteAccount(
        address: string,
    ): Promise<{ success: boolean; error?: string }> {
        const enc = encodeURIComponent(address);
        return this.request(`/accounts/${enc}`, { method: 'DELETE' });
    }
}
